import React from "react";
import { Tooltip } from "@mui/material";
import { ChevronsLeft, ChevronRight } from "lucide-react";
import ActingUserStrip from "./ActingUserStrip";
import UserInfoCard from "./UserInfoCard";
import CollapsedSidebarHeader from "./CollapsedSidebarHeader";

const SidebarHeaderAccountSwitcherButton = ({ isAccountSwitcherOpen, onToggleAccountSwitcher, displayAccount }) => {
  const chevronClassName = `chevron-icon${isAccountSwitcherOpen ? " open" : ""}`;

  return (
    <button
      className="account-switcher-button"
      onClick={onToggleAccountSwitcher}
      aria-label="Switch customer account"
      style={{ cursor: "pointer" }}
    >
      {/* Avatar */}
      <div
        className="account-avatar"
        style={{
          background: "#9D6AC4", // Deep Purple from design system
        }}
      >
        <span
          className="account-initial"
          style={{
            color: "#ffffff",
            WebkitTextFillColor: "#ffffff",
            textFillColor: "#ffffff",
            display: "block",
          }}
        >
          {displayAccount?.initial || displayAccount?.name?.charAt(0)?.toUpperCase() || "U"}
        </span>
      </div>

      {/* User Info */}
      <div className="account-info">
        <div className="account-name" title={displayAccount?.name || "User"}>
          {displayAccount?.name || "User"}
        </div>
        <div className="account-email" title={displayAccount?.email || "user@horaizen.com"}>
          {displayAccount?.email || "user@horaizen.com"}
        </div>
      </div>

      {/* Chevron Icon */}
      <ChevronRight className={chevronClassName} />
    </button>
  );
};

const SidebarHeaderMain = ({
  canSwitchCustomer,
  showCustomerSwitcher,
  isAccountSwitcherOpen,
  onToggleAccountSwitcher,
  displayAccount,
  isCollapsed,
  isMobile,
}) => {
  const infoCollapsed = isCollapsed && !isMobile;

  if (canSwitchCustomer && showCustomerSwitcher) {
    return (
      <SidebarHeaderAccountSwitcherButton
        isAccountSwitcherOpen={isAccountSwitcherOpen}
        onToggleAccountSwitcher={onToggleAccountSwitcher}
        displayAccount={displayAccount}
      />
    );
  }

  // Brand button owns home nav; wrapping the card nests the role selector and redirects on open.
  return <UserInfoCard isCollapsed={infoCollapsed} />;
};

const SidebarHeader = ({
  isCollapsed,
  isMobile,
  canSwitchCustomer,
  showCustomerSwitcher = true,
  isAccountSwitcherOpen,
  setIsAccountSwitcherOpen,
  displayAccount,
  defaultLandingPath,
  onNavigateHome,
  onActingUserChange,
  isActingUserPickerOpen,
  setIsActingUserPickerOpen,
  onStopActingAsUser,
  onToggleCollapse,
}) => {
  const headerClassName = `sidebar-header${isCollapsed && !isMobile ? " collapsed" : ""}`;
  const showActingStrip = canSwitchCustomer && onActingUserChange;
  const showTopHeaderStrip = !(canSwitchCustomer && !showCustomerSwitcher);
  const collapseAriaLabel = isCollapsed ? "Expand sidebar" : "Collapse sidebar";
  const collapseTooltipLabel = isCollapsed ? "Expand menu" : "Collapse menu";
  const collapseTooltipPlacement = isMobile ? "bottom" : isCollapsed ? "right" : "left";

  const toggleAccountSwitcher = () => {
    setIsAccountSwitcherOpen((prev) => {
      const next = !prev;
      if (next) setIsActingUserPickerOpen(false);
      return next;
    });
  };

  const toggleActingUserPicker = () => {
    setIsActingUserPickerOpen((prev) => {
      const next = !prev;
      if (next) setIsAccountSwitcherOpen(false);
      return next;
    });
  };

  return (
    <>
      {!isMobile && !isCollapsed && (
        <Tooltip title={collapseTooltipLabel} placement={collapseTooltipPlacement} arrow>
          <button
            type="button"
            className="sidebar-floating-collapse-button sidebar-toggle-control"
            onClick={onToggleCollapse}
            aria-label={collapseAriaLabel}
            aria-expanded={!isCollapsed}
          >
            <ChevronsLeft className="toggle-icon" />
          </button>
        </Tooltip>
      )}

      {isCollapsed && !isMobile ? (
        <CollapsedSidebarHeader
          onToggleCollapse={onToggleCollapse}
          collapseAriaLabel={collapseAriaLabel}
          collapseTooltipLabel={collapseTooltipLabel}
          collapseTooltipPlacement={collapseTooltipPlacement}
          isCollapsed={isCollapsed}
        />
      ) : (
        <div className="sidebar-top-header">
          <button
            type="button"
            className="sidebar-brand-button"
            onClick={onNavigateHome}
            aria-label={`Go to home (${defaultLandingPath})`}
          >
            <img src="/icon-square-gradient.svg" alt="horAIzen icon" className="sidebar-brand-icon" />
            <span className="sidebar-brand-text" aria-hidden="true">
              <span className="sidebar-brand-hor">hor</span>
              <span className="sidebar-brand-alzen">Alzen</span>
            </span>
          </button>
        </div>
      )}

      {showTopHeaderStrip ? (
        <div className={headerClassName}>
          <SidebarHeaderMain
            canSwitchCustomer={canSwitchCustomer}
            showCustomerSwitcher={showCustomerSwitcher}
            isAccountSwitcherOpen={isAccountSwitcherOpen}
            onToggleAccountSwitcher={toggleAccountSwitcher}
            displayAccount={displayAccount}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
          />
        </div>
      ) : null}
      {showActingStrip && (
        <ActingUserStrip
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          isActingUserPickerOpen={isActingUserPickerOpen}
          onToggleActingUserPicker={toggleActingUserPicker}
          onStopActing={onStopActingAsUser}
        />
      )}
    </>
  );
};

export default SidebarHeader;
